// @ts-ignore
import { Launcher } from 'react-chat-window';
import React, { useState } from 'react';
import images from 'assets/images-base';
import {
  createSession,
  sendChatMessage,
  sendChatMessageStream,
  getSessions,
  getSessionHistory,
  updateSessionTitle,
  deleteSession,
} from 'api/chatBot';
import { parseStreamingEvent, formatStreamingContent, mapPositionToRole, isNumeric } from 'helper/ChatBot';
import {
  ChatBotWrapper,
  SuggestionWrapper,
  TextLoading,
  ClickableTitle,
  ControlsContainer,
  ControlButton,
} from './styled';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import { MessageHistory } from 'types/interface';
import SessionStorageUtil, { COMMON_KEY } from 'helper/SessionStorageUtil';

type Message = {
  author: string;
  type: string;
  data: any;
};

type MessageList = {
  messages: Message[];
  newMessageCount?: number;
};

const Loading: React.FC = () => <TextLoading src={images.chatbot.TextLoading} />;

const messageLoading: Message = {
  author: 'them',
  type: 'text',
  data: { text: <Loading /> },
};

const ChatBot = () => {
  const { t } = useTranslation();
  const initialSuggestions = [t('chatbot.suggestion-1'), t('chatbot.suggestion-2')];

  const initialMessage: MessageList = {
    messages: [
      {
        author: 'them',
        type: 'text',
        data: { text: t('chatbot.welcome') },
      },
    ],
  };
  const [messageList, setMessageList] = useState<MessageList>(initialMessage);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [sessionTitle, setSessionTitle] = useState<string>('Wiki Support Chat');
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editTitleValue, setEditTitleValue] = useState<string>('');

  // Get user info from session storage
  const userInfo = SessionStorageUtil.getItemObject(COMMON_KEY.userInfo, {});
  const userId = userInfo?.user_id?.toString();
  const userEmail = userInfo?.email;
  const userName = userInfo?.name;
  const userPosition = userInfo?.position_name;

  // Map position to valid roles
  const userRole = mapPositionToRole(userPosition);

  // Convert API message history to component message format
  const convertHistoryToMessages = (history: MessageHistory[]): Message[] => {
    return history.map((msg) => ({
      author: msg.type === 'user' ? 'me' : 'them',
      type: 'text',
      data: { text: msg.content },
    }));
  };

  // Load session history
  const loadSessionHistory = async (sessionId: string) => {
    try {
      const response = await getSessionHistory(sessionId, 50);
      const history = response.data.messages;

      if (history.length > 0) {
        const historyMessages = convertHistoryToMessages(history);
        setMessageList({
          messages: [
            {
              author: 'them',
              type: 'text',
              data: { text: t('chatbot.welcome') },
            },
            ...historyMessages,
          ],
        });
      }
    } catch (error) {
      console.error('Failed to load session history:', error);
    }
  };

  // Check for existing active session
  const checkExistingSession = async () => {
    try {
      const response = await getSessions(userId, 20);
      const sessions = response.data.sessions || [];
      const activeSession = sessions.find((session: any) => session.status === 'active');

      if (activeSession) {
        setSessionTitle(activeSession.title);
        return activeSession.session_id;
      }
      return null;
    } catch (error) {
      console.error('Failed to check existing session:', error);
      return null;
    }
  };

  const createNewSession = async () => {
    try {
      const sessionData = {
        user: {
          user_id: userId,
          user_type: 'authenticated' as const,
          username: userName,
          email: userEmail,
          roles: [userRole],
        },
        title: 'Wiki Support Chat',
      };

      const response = await createSession(sessionData);
      setSessionId(response.data.session.session_id);
      setSessionTitle(response.data.session.title);
      return response.data.session.session_id;
    } catch (error) {
      console.error('Failed to create session:', error);
      return null;
    }
  };

  const initializeSession = async () => {
    const existingSession = await checkExistingSession();

    if (existingSession) {
      setSessionId(existingSession);
      await loadSessionHistory(existingSession);
      return existingSession;
    } else {
      return await createNewSession();
    }
  };

  const updateLoadingMessage = (text: string) => {
    const formattedText = formatStreamingContent(text);
    setMessageList((prev) => ({
      ...prev,
      messages: prev.messages.map((msg, index) =>
        index === prev.messages.length - 1 && msg.author === 'them' && React.isValidElement(msg.data.text)
          ? { ...msg, data: { text: formattedText } }
          : msg
      ),
    }));
  };

  const removeLoadingMessage = () => {
    setMessageList((prev) => ({
      ...prev,
      messages: prev.messages.filter((msg, index) => !(index === prev.messages.length - 1 && msg.author === 'them')),
    }));
  };

  const startStreamingChat = async (chatData: any, newMessage: Message) => {
    try {
      setIsStreaming(true);

      const response = await sendChatMessageStream(chatData);

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let finalAnswer = '';
        let finalEventProcessed = false;
        let buffer = ''; // Buffer for incomplete chunks

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // If stream ends without final event, use the last progress as final
            if (!finalEventProcessed && finalAnswer) {
              removeLoadingMessage();
              sentMessage(finalAnswer, newMessage, '');
            }
            break;
          }

          const chunk = decoder.decode(value);
          buffer += chunk; // Add new chunk to buffer

          const lines = buffer.split('\n');

          // Keep the last line in buffer if it's incomplete
          const completeLines = lines.slice(0, -1);
          buffer = lines[lines.length - 1];

          for (const line of completeLines) {
            if (line.trim() === '' || !line.startsWith('data: ')) continue;

            const event = parseStreamingEvent(line);

            if (event) {
              switch (event.type) {
                case 'start':
                  if (event.message) {
                    updateLoadingMessage(event.message);
                  }
                  break;

                case 'progress':
                  if (event.partial_answer) {
                    const displayText = event.message
                      ? `${event.message}\n\n${event.partial_answer}`
                      : event.partial_answer;
                    updateLoadingMessage(displayText);
                  } else if (event.message) {
                    updateLoadingMessage(event.message);
                  }
                  break;

                case 'final':
                  removeLoadingMessage();
                  if (event.final_answer) {
                    finalAnswer = event.final_answer; // Store final answer
                    sentMessage(event.final_answer, newMessage, '');
                  }

                  if (event.clarification_needed && event.ask_clarification_questions) {
                    setSuggestions(event.ask_clarification_questions);
                  }
                  finalEventProcessed = true;
                  break;

                case 'error':
                  removeLoadingMessage();
                  sentMessage(t('chatbot.error'), newMessage, '');
                  break;
              }
            }
          }
        }
      } else {
        removeLoadingMessage();
        sentMessage(t('chatbot.error'), newMessage, '');
      }
    } catch (err) {
      removeLoadingMessage();
      sentMessage(t('chatbot.error'), newMessage, '');
    } finally {
      setIsStreaming(false);
    }
  };

  const sendRegularChat = async (chatData: any, newMessage: Message) => {
    try {
      const response = await sendChatMessage(chatData);
      const { final_answer, clarification_needed, ask_clarification_questions } = response.data;

      removeLoadingMessage();

      if (clarification_needed && ask_clarification_questions) {
        setSuggestions(ask_clarification_questions);
      }

      if (final_answer) {
        sentMessage(final_answer, newMessage, '');
      } else {
        sentMessage(t('chatbot.noAnswer'), newMessage, '');
      }
    } catch (error) {
      console.error('Regular chat error:', error);
      removeLoadingMessage();
      sentMessage(t('chatbot.error'), newMessage, '');
    }
  };

  const onMessageWasSent = async (message: Message) => {
    setSuggestions([]);

    const newMessage: Message = {
      author: message.author,
      type: message.type,
      data: {
        text: message.data.text.trim(),
      },
    };

    if (newMessage.data.text.length > 0) {
      setMessageList({
        ...messageList,
        messages: [...messageList.messages, newMessage, messageLoading],
      });

      try {
        let currentSessionId = sessionId;
        if (!currentSessionId) {
          const newSessionId = await initializeSession();
          if (!newSessionId) {
            sentMessage(t('chatbot.error'), newMessage, '');
            return;
          }
          currentSessionId = newSessionId;
        }

        const chatData = {
          user_id: userId,
          thread_id: currentSessionId,
          original_query: message.data.text,
          user_roles: [userRole],
        };

        if (process.env.REACT_APP_API_CHATBOT_MODE === 'regular') {
          await sendRegularChat(chatData, newMessage);
        } else {
          await startStreamingChat(chatData, newMessage);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        sentMessage(t('chatbot.error'), newMessage, '');
      }
    }
  };

  const onFilesSelected = (fileList: any) => {
    const objectURL = window.URL.createObjectURL(fileList[0]);

    setMessageList({
      ...messageList,
      messages: [
        ...messageList.messages,
        {
          author: 'me',
          type: 'file',
          data: {
            url: objectURL,
            fileName: fileList[0].name,
          },
        },
      ],
    });
  };

  const sentMessage = (text: string, message: Message, id: string) => {
    if (text.trim().length > 0) {
      const formattedText = formatStreamingContent(text);
      setMessageList({
        ...messageList,
        messages: [
          ...messageList.messages,
          message,
          {
            author: 'them',
            type: 'text',
            data: {
              text: (
                <>
                  <span dangerouslySetInnerHTML={{ __html: formattedText }} />
                  {isNumeric(id) && (
                    <a
                      href={`https://wiki.amela.vn/home/detail-post/${id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detail"
                    >
                      {t('chatbot.detail')}
                    </a>
                  )}
                </>
              ),
            },
          },
        ],
      });
    }
  };

  const handelClick = async () => {
    setMessageList({ ...messageList, newMessageCount: 0 });
    setIsOpen((prev) => !prev);

    if (isOpen && isStreaming) {
      setIsStreaming(false);
    }

    if (!isOpen && messageList.messages.length <= 1) {
      if (!sessionId) {
        await initializeSession();
      }
      setSuggestions(initialSuggestions);
    }

    if (isOpen && messageList.messages.length <= 1) setSuggestions([]);
  };

  const handleChooseSuggestion = (suggestion: string) => {
    const messageSuggestion = { author: 'me', type: 'text', data: { text: suggestion } };
    onMessageWasSent(messageSuggestion);
  };

  const handleUpdateSessionTitle = async (newTitle: string) => {
    if (!sessionId) return;

    try {
      const response = await updateSessionTitle(sessionId, { title: newTitle });
      setSessionTitle(response.data.title);
      setIsEditingTitle(false);
      setEditTitleValue('');
    } catch (error) {}
  };

  const startEditingTitle = () => {
    if (sessionId) {
      setIsEditingTitle(true);
      setEditTitleValue(sessionTitle);
    }
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
    setEditTitleValue('');
  };

  const saveTitle = () => {
    if (editTitleValue.trim()) {
      if (editTitleValue.trim() !== sessionTitle) {
        handleUpdateSessionTitle(editTitleValue.trim());
      } else {
        cancelEditingTitle();
      }
    } else {
      cancelEditingTitle();
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionId || !userId) return;

    try {
      await deleteSession(sessionId, { user_id: userId });

      setSessionId('');
      setSessionTitle('Wiki Support Chat');
      setMessageList(initialMessage);
      setSuggestions([]);

      await initializeSession();
    } catch (error) {}
  };

  return (
    <ChatBotWrapper suggestions={suggestions.length}>
      <Launcher
        agentProfile={{
          teamName: isEditingTitle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={editTitleValue}
                onChange={(e) => setEditTitleValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') saveTitle();
                  if (e.key === 'Escape') cancelEditingTitle();
                }}
                onBlur={saveTitle}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700',
                  outline: 'none',
                  borderBottom: '1px solid white',
                }}
                autoFocus
              />
            </div>
          ) : (
            <ClickableTitle onClick={startEditingTitle}>
              {sessionTitle}
              <span className="edit-icon" role="img" aria-label="Edit title">
                ✏️
              </span>
            </ClickableTitle>
          ),
          imageUrl: images.chatbot.ChatBotAva,
        }}
        onMessageWasSent={onMessageWasSent}
        onFilesSelected={onFilesSelected}
        messageList={messageList.messages}
        showEmoji={false}
        mute={false}
        handleClick={handelClick}
        isOpen={isOpen}
      />

      <SuggestionWrapper isOpen={isOpen}>
        {suggestions &&
          suggestions.map((suggestion, index) => {
            return (
              suggestion && (
                <Tag
                  key={index}
                  className="tag-suggestion"
                  color="blue"
                  onClick={() => handleChooseSuggestion(suggestion)}
                >
                  {suggestion}
                </Tag>
              )
            );
          })}
      </SuggestionWrapper>

      {/* Controls below chat window */}
      {isOpen && (
        <ControlsContainer>
          {sessionId && (
            <ControlButton onClick={handleDeleteSession}>
              <span role="img" aria-label="Delete session">
                🗑️
              </span>
              Xóa
            </ControlButton>
          )}
        </ControlsContainer>
      )}
    </ChatBotWrapper>
  );
};

export default ChatBot;
